import type { Surfbreak } from "@/data/surfbreaks";

export type Conditions = {
  waveHeight: number; // m
  wavePeriod: number; // s
  waveDirection: number; // deg
  swellHeight: number;
  swellPeriod: number;
  windSpeed: number; // km/h
  windDirection: number;
  windGusts: number;
  airTemp: number; // C
  weatherCode: number;
  seaTemp: number; // C
};

export type HourlyPoint = {
  time: string; // ISO
  waveHeight: number;
  wavePeriod: number;    // s   — was missing
  waveDirection: number; // deg — was missing
  swellHeight: number;
  swellPeriod: number;
  windSpeed: number;     // km/h
  windDirection: number;
  windGusts: number;     // km/h — was missing
  airTemp: number;       // °C  — was missing
  weatherCode: number;   //     — was missing
};

export type DailyPoint = {
  date: string; // ISO date YYYY-MM-DD
  waveHeightMax: number;
  waveHeightMean: number;
  swellHeightMax: number;
  swellPeriodMax: number;
  waveDirection: number;
  windSpeedMax: number;
  windDirection: number;
  weatherCode: number;
  tempMax: number;
  tempMin: number;
};

export type ForecastBundle = {
  current: Conditions;
  hourly: HourlyPoint[]; // next 24h from now
  daily: DailyPoint[];
};

// ---------------------------------------------------------------------------
// In-memory cache — keyed by "lat,lon", TTL 10 minutes
// ---------------------------------------------------------------------------
const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry {
  bundle: ForecastBundle;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();

function cacheKey(lat: number, lon: number) {
  // Round to 2 dp so nearby repeated calls share a cache slot
  return `${lat.toFixed(2)},${lon.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Internal helper — fetch JSON with timeout and proper error checking
// ---------------------------------------------------------------------------
const FETCH_TIMEOUT_MS = 8_000;

async function fetchJson(url: string, label: string): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(url, { signal: controller.signal });
  } catch (err) {
    const msg = (err as Error).message;
    throw new Error(
      controller.signal.aborted
        ? `${label}: timed out after ${FETCH_TIMEOUT_MS / 1000}s`
        : `${label}: network error — ${msg}`,
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "(unreadable body)");
    throw new Error(`${label}: HTTP ${res.status} — ${body}`);
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new Error(`${label}: response is not valid JSON`);
  }

  // Open-Meteo returns { error: true, reason: "..." } on bad params
  if (
    json !== null &&
    typeof json === "object" &&
    "error" in json &&
    (json as Record<string, unknown>).error === true
  ) {
    const reason = (json as Record<string, unknown>).reason ?? JSON.stringify(json);
    throw new Error(`${label}: API error — ${reason}`);
  }

  return json;
}

// ---------------------------------------------------------------------------
// Yr.no (met.no) fallback — weather only (wind, temp, weather code)
// Returns data shaped to match the Open-Meteo weather response used below.
// Docs: https://api.met.no/weatherapi/locationforecast/2.0/documentation
// ---------------------------------------------------------------------------

/** Subset of the Yr.no locationforecast/2.0/compact response we care about */
interface YrTimeseries {
  time: string; // ISO-8601
  data: {
    instant: { details: Record<string, number> };
    next_1_hours?: { summary: { symbol_code: string }; details: Record<string, number> };
    next_6_hours?: { summary: { symbol_code: string }; details: Record<string, number> };
  };
}

/** Map a Yr.no symbol_code to an Open-Meteo WMO weather_code (best-effort) */
function yrSymbolToWmoCode(symbol: string): number {
  if (symbol.includes("thunder"))          return 95;
  if (symbol.includes("heavyrain"))        return 65;
  if (symbol.includes("rain"))             return 61;
  if (symbol.includes("lightrain"))        return 51;
  if (symbol.includes("sleet"))            return 67;
  if (symbol.includes("snow"))             return 71;
  if (symbol.includes("fog"))              return 45;
  if (symbol.includes("cloudy"))           return 3;
  if (symbol.includes("partlycloudy"))     return 2;
  if (symbol.includes("fair"))             return 1;
  if (symbol.includes("clearsky"))         return 0;
  return 3; // default: overcast
}

async function fetchYrWeather(
  lat: number,
  lon: number,
  forecastDays: number,
): Promise<Record<string, unknown>> {
  // Yr.no requires a descriptive User-Agent — anonymous requests may be blocked
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(
      `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`,
      {
        signal: controller.signal,
        headers: {
          "User-Agent": "SurfApp/1.0 github.com/your-org/your-repo",
        },
      },
    );
  } catch (err) {
    const msg = (err as Error).message;
    throw new Error(
      controller.signal.aborted
        ? `Yr.no Weather: timed out after ${FETCH_TIMEOUT_MS / 1000}s`
        : `Yr.no Weather: network error — ${msg}`,
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "(unreadable body)");
    throw new Error(`Yr.no Weather: HTTP ${res.status} — ${body}`);
  }

  const yr = (await res.json()) as { properties: { timeseries: YrTimeseries[] } };
  const series: YrTimeseries[] = yr.properties?.timeseries ?? [];

  // --- current (first entry) ---
  const first = series[0];
  const firstDetails = first?.data?.instant?.details ?? {};
  const firstSymbol =
    first?.data?.next_1_hours?.summary?.symbol_code ??
    first?.data?.next_6_hours?.summary?.symbol_code ??
    "cloudy";

  const current = {
    temperature_2m:    firstDetails["air_temperature"]    ?? 0,
    wind_speed_10m:    (firstDetails["wind_speed"]        ?? 0) * 3.6, // m/s → km/h
    wind_direction_10m: firstDetails["wind_from_direction"] ?? 0,
    wind_gusts_10m:    (firstDetails["wind_speed_of_gust"] ?? firstDetails["wind_speed"] ?? 0) * 3.6,
    weather_code:      yrSymbolToWmoCode(firstSymbol),
  };

  // --- hourly arrays (next forecastDays * 24 hours) ---
  const maxHours = forecastDays * 24;
  // Yr.no timestamps include a 'Z'; slice by count is fine since they're already in order
  const hourlySlice = series.slice(0, maxHours);

  const hTime:          string[] = [];
  const hWindSpeed:     number[] = [];
  const hWindDirection: number[] = [];
  const hWindGusts:     number[] = [];
  const hTemp:          number[] = [];
  const hWeatherCode:   number[] = [];

  for (const s of hourlySlice) {
    hTime.push(s.time);
    const d = s.data.instant.details;
    hWindSpeed.push((d["wind_speed"] ?? 0) * 3.6);
    hWindDirection.push(d["wind_from_direction"] ?? 0);
    hWindGusts.push((d["wind_speed_of_gust"] ?? d["wind_speed"] ?? 0) * 3.6);
    hTemp.push(d["air_temperature"] ?? 0);
    const sym =
      s.data.next_1_hours?.summary?.symbol_code ??
      s.data.next_6_hours?.summary?.symbol_code ?? "cloudy";
    hWeatherCode.push(yrSymbolToWmoCode(sym));
  }

  // --- daily aggregates ---
  // Group by calendar date
  const dayMap = new Map<
    string,
    { temps: number[]; windSpeeds: number[]; windDirs: number[]; symbols: string[] }
  >();

  for (const s of series) {
    const day = s.time.slice(0, 10);
    if (!dayMap.has(day)) dayMap.set(day, { temps: [], windSpeeds: [], windDirs: [], symbols: [] });
    const bucket = dayMap.get(day)!;
    const d = s.data.instant.details;
    bucket.temps.push(d["air_temperature"] ?? 0);
    bucket.windSpeeds.push((d["wind_speed"] ?? 0) * 3.6);
    bucket.windDirs.push(d["wind_from_direction"] ?? 0);
    const sym =
      s.data.next_1_hours?.summary?.symbol_code ??
      s.data.next_6_hours?.summary?.symbol_code;
    if (sym) bucket.symbols.push(sym);
  }

  const sortedDays = [...dayMap.keys()].sort().slice(0, forecastDays);

  const dTime:          string[] = [];
  const dWeatherCode:   number[] = [];
  const dTempMax:       number[] = [];
  const dTempMin:       number[] = [];
  const dWindSpeedMax:  number[] = [];
  const dWindDirDom:    number[] = [];

  for (const day of sortedDays) {
    const b = dayMap.get(day)!;
    dTime.push(day);
    dTempMax.push(Math.max(...b.temps));
    dTempMin.push(Math.min(...b.temps));
    dWindSpeedMax.push(Math.max(...b.windSpeeds));
    // Dominant wind direction: circular mean
    const sinSum = b.windDirs.reduce((s, d) => s + Math.sin((d * Math.PI) / 180), 0);
    const cosSum = b.windDirs.reduce((s, d) => s + Math.cos((d * Math.PI) / 180), 0);
    dWindDirDom.push(Math.round(((Math.atan2(sinSum, cosSum) * 180) / Math.PI + 360) % 360));
    // Most common symbol → WMO code
    const freq = new Map<string, number>();
    b.symbols.forEach(sym => freq.set(sym, (freq.get(sym) ?? 0) + 1));
    const dominantSym = [...freq.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "cloudy";
    dWeatherCode.push(yrSymbolToWmoCode(dominantSym));
  }

  // Return a shape that mirrors the Open-Meteo /v1/forecast response
  return {
    current,
    hourly: {
      time:               hTime,
      wind_speed_10m:     hWindSpeed,
      wind_direction_10m: hWindDirection,
      wind_gusts_10m:     hWindGusts,
      temperature_2m:     hTemp,
      weather_code:       hWeatherCode,
    },
    daily: {
      time:                          dTime,
      weather_code:                  dWeatherCode,
      temperature_2m_max:            dTempMax,
      temperature_2m_min:            dTempMin,
      wind_speed_10m_max:            dWindSpeedMax,
      wind_direction_10m_dominant:   dWindDirDom,
    },
  };
}

/**
 * Fetch weather with automatic fallback:
 *   1. Try Open-Meteo /v1/forecast
 *   2. On any error (5xx, network, timeout) → try Yr.no
 *   3. If both fail, throw a combined error
 */
async function fetchWeatherWithFallback(
  lat: number,
  lon: number,
  forecastDays: number,
): Promise<Record<string, unknown>> {
  const wxUrl =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m,weather_code` +
    `&hourly=wind_speed_10m,wind_direction_10m,wind_gusts_10m,temperature_2m,weather_code` +
    `&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,wind_direction_10m_dominant` +
    `&forecast_days=${forecastDays}`;

  try {
    return (await fetchJson(wxUrl, "Weather API")) as Record<string, unknown>;
  } catch (primaryErr) {
    console.warn(
      `[surf] Open-Meteo weather failed (${(primaryErr as Error).message}), trying Yr.no fallback…`,
    );
    try {
      return await fetchYrWeather(lat, lon, forecastDays);
    } catch (fallbackErr) {
      throw new Error(
        `Weather data unavailable.\n` +
        `  • Open-Meteo: ${(primaryErr as Error).message}\n` +
        `  • Yr.no:      ${(fallbackErr as Error).message}`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// fetchConditions — with cache and abort timeout
// ---------------------------------------------------------------------------
export async function fetchConditions(b: { lat: number; lon: number }): Promise<ForecastBundle> {
  // Guard against undefined / NaN coordinates which produce silent 400s
  if (b.lat == null || b.lon == null || !isFinite(b.lat) || !isFinite(b.lon)) {
    throw new Error(`fetchConditions: invalid coordinates (lat=${b.lat}, lon=${b.lon})`);
  }

  // Return cached bundle if still fresh
  const key = cacheKey(b.lat, b.lon);
  const cached = cache.get(key);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.bundle;
  }

  // 7 days is enough — halves payload vs 16, cold start is noticeably faster
  const FORECAST_DAYS = 16;

  const marineUrl =
    `https://marine-api.open-meteo.com/v1/marine` +
    `?latitude=${b.lat}&longitude=${b.lon}` +
    `&current=wave_height,wave_direction,wave_period,swell_wave_height,swell_wave_period,sea_surface_temperature` +
    `&hourly=wave_height,wave_period,wave_direction,swell_wave_height,swell_wave_period` +
    `&daily=wave_height_max,wave_direction_dominant,wave_period_max,swell_wave_height_max,swell_wave_period_max` +
    `&forecast_days=${FORECAST_DAYS}`;

  const [m, w] = (await Promise.all([
    fetchJson(marineUrl, "Marine API"),
    fetchWeatherWithFallback(b.lat, b.lon, FORECAST_DAYS),
  ])) as [Record<string, unknown>, Record<string, unknown>];

  // Convenience typed accessors
  const mc = m.current as Record<string, number> | undefined;
  const wc = w.current as Record<string, number> | undefined;
  const mh = m.hourly  as Record<string, unknown[]> | undefined;
  const wh = w.hourly  as Record<string, unknown[]> | undefined;
  const md = m.daily   as Record<string, unknown[]> | undefined;
  const wd = w.daily   as Record<string, unknown[]> | undefined;

  // -------------------------------------------------------------------------
  // Current conditions
  // -------------------------------------------------------------------------
  const current: Conditions = {
    waveHeight:    mc?.wave_height            ?? 0,
    wavePeriod:    mc?.wave_period            ?? 0,
    waveDirection: mc?.wave_direction         ?? 0,
    swellHeight:   mc?.swell_wave_height      ?? 0,
    swellPeriod:   mc?.swell_wave_period      ?? 0,
    windSpeed:     wc?.wind_speed_10m         ?? 0,
    windDirection: wc?.wind_direction_10m     ?? 0,
    windGusts:     wc?.wind_gusts_10m         ?? 0,
    airTemp:       wc?.temperature_2m         ?? 0,
    weatherCode:   wc?.weather_code           ?? 0,
    seaTemp:       mc?.sea_surface_temperature ?? 0,
  };

  // -------------------------------------------------------------------------
  // Hourly — next 24 h from ~now
  // -------------------------------------------------------------------------
  const mTimes = (mh?.time ?? []) as string[];
  const wTimes = (wh?.time ?? []) as string[];

  // Open-Meteo timestamps are UTC but formatted WITHOUT a "Z" suffix
  // (e.g. "2024-06-04T14:00"). new Date() treats those as *local* time,
  // so on any non-UTC machine the start index would be wrong. Fix: append
  // "Z" before parsing so JS always treats them as UTC.
  const toUtcMs = (t: string) => new Date(t.endsWith("Z") ? t : t + "Z").getTime();

  const now = Date.now();
  // Anchor to the start of the current UTC hour so we never show a partial
  // past hour. The -30 min grace keeps the current hour visible rather than
  // jumping straight to the next one.
  const startIdx = mTimes.findIndex(t => toUtcMs(t) >= now - 30 * 60 * 1000);
  const start = startIdx === -1 ? 0 : startIdx;

  // Join weather hourly data by UTC epoch (not raw string) so format
  // differences between APIs can't silently zero out wind readings.
  const wTimeEpochIndex = new Map<number, number>(
    wTimes.map((t, i) => [toUtcMs(t), i]),
  );

  const hourly: HourlyPoint[] = [];
  for (let i = start; i < Math.min(start + 24, mTimes.length); i++) {
    const t = mTimes[i];
    const wi = wTimeEpochIndex.get(toUtcMs(t)) ?? -1;
    hourly.push({
      time:          t,
      waveHeight:    (mh?.wave_height?.[i]          as number) ?? 0,
      wavePeriod:    (mh?.wave_period?.[i]           as number) ?? 0,
      waveDirection: (mh?.wave_direction?.[i]        as number) ?? 0,
      swellHeight:   (mh?.swell_wave_height?.[i]     as number) ?? 0,
      swellPeriod:   (mh?.swell_wave_period?.[i]     as number) ?? 0,
      windSpeed:     wi >= 0 ? (wh?.wind_speed_10m?.[wi]      as number) ?? 0 : 0,
      windDirection: wi >= 0 ? (wh?.wind_direction_10m?.[wi]  as number) ?? 0 : 0,
      windGusts:     wi >= 0 ? (wh?.wind_gusts_10m?.[wi]      as number) ?? 0 : 0,
      airTemp:       wi >= 0 ? (wh?.temperature_2m?.[wi]      as number) ?? 0 : 0,
      weatherCode:   wi >= 0 ? (wh?.weather_code?.[wi]        as number) ?? 0 : 0,
    });
  }

  // -------------------------------------------------------------------------
  // Daily — compute waveHeightMean from hourly buckets per day
  // -------------------------------------------------------------------------
  const mDates = (md?.time ?? []) as string[];
  const wDates = (wd?.time ?? []) as string[];

  // Build a day → average wave height map from hourly data
  const dailyMeanMap: Record<string, { sum: number; count: number }> = {};
  mTimes.forEach((t, i) => {
    const day = t.slice(0, 10); // "YYYY-MM-DD"
    const val = (mh?.wave_height?.[i] as number) ?? 0;
    if (!dailyMeanMap[day]) dailyMeanMap[day] = { sum: 0, count: 0 };
    dailyMeanMap[day].sum += val;
    dailyMeanMap[day].count += 1;
  });

  // Pre-build wDates index map — dates are plain YYYY-MM-DD strings so no UTC issue here
  const wDatesIndex = new Map<string, number>(wDates.map((d, i) => [d.slice(0, 10), i]));

  const daily: DailyPoint[] = mDates.map((d, i) => {
    const wi = wDatesIndex.get(d) ?? -1;
    const meanBucket = dailyMeanMap[d];
    const waveHeightMean =
      meanBucket && meanBucket.count > 0
        ? Math.round((meanBucket.sum / meanBucket.count) * 100) / 100
        : ((md?.wave_height_max?.[i] as number) ?? 0);

    return {
      date:           d,
      waveHeightMax:  (md?.wave_height_max?.[i]         as number) ?? 0,
      waveHeightMean,
      swellHeightMax: (md?.swell_wave_height_max?.[i]   as number) ?? 0,
      swellPeriodMax: (md?.swell_wave_period_max?.[i]   as number) ?? 0,
      waveDirection:  (md?.wave_direction_dominant?.[i] as number) ?? 0,
      windSpeedMax:    wi >= 0 ? (wd?.wind_speed_10m_max?.[wi]          as number) ?? 0 : 0,
      windDirection:   wi >= 0 ? (wd?.wind_direction_10m_dominant?.[wi] as number) ?? 0 : 0,
      weatherCode:     wi >= 0 ? (wd?.weather_code?.[wi]                as number) ?? 0 : 0,
      tempMax:         wi >= 0 ? (wd?.temperature_2m_max?.[wi]          as number) ?? 0 : 0,
      tempMin:         wi >= 0 ? (wd?.temperature_2m_min?.[wi]          as number) ?? 0 : 0,
    };
  });

  const bundle: ForecastBundle = { current, hourly, daily };

  // Store in cache
  cache.set(key, { bundle, expiresAt: Date.now() + CACHE_TTL_MS });

  return bundle;
}

// Manually invalidate cache for a location (e.g. on pull-to-refresh)
export function invalidateConditionsCache(b: { lat: number; lon: number }) {
  cache.delete(cacheKey(b.lat, b.lon));
}

// ---------------------------------------------------------------------------
// Board recommendation
// ---------------------------------------------------------------------------
export type BoardRec = {
  type: string;
  length: string;
  volume: string;
  fins: string;
  why: string;
  emoji: string;
};

export function recommendBoard(c: Conditions, b: Surfbreak): BoardRec {
  const h = c.waveHeight;
  if (h < 0.6) {
    return { emoji: "🏄‍♂️", type: "Longboard / Log", length: "9'0\" – 9'6\"", volume: "70–85L", fins: "Single fin or 2+1", why: "Tiny, gutless surf — paddle power and glide are everything. Cruise and noseride." };
  }
  if (h < 1.0) {
    return { emoji: "🥚", type: "Mid-length / Egg", length: "7'0\" – 7'6\"", volume: "50–60L", fins: "2+1 or Twin", why: "Small, mushy waves. A mid lets you catch everything and still draw fun lines." };
  }
  if (h < 1.5) {
    return { emoji: "🌊", type: "Groveler / Hybrid", length: "5'6\" – 5'10\"", volume: "30–34L", fins: "Twin or Quad", why: "Playful waist-to-shoulder-high surf. Wide tail keeps you fast through flat sections." };
  }
  if (h < 2.2) {
    return { emoji: "⚡", type: "High-Performance Shortboard", length: "5'10\" – 6'2\"", volume: "27–30L", fins: "Thruster", why: "Clean head-high+ surf — your everyday performance shred stick." };
  }
  if (h < 3.0) {
    return { emoji: "🔥", type: "Step-Up", length: "6'4\" – 6'8\"", volume: "30–34L", fins: "Thruster or Quad", why: "Solid overhead. More paddle, hold, and control on bigger faces." };
  }
  if (h < 4.5) {
    return { emoji: "🌀", type: "Semi-Gun", length: "6'10\" – 7'6\"", volume: "34–40L", fins: "Thruster", why: "Heavy double-overhead. Pointier nose for late drops and steep takeoffs." };
  }
  return {
    emoji: "🚀",
    type: b.id === "nazare" || b.id === "mavericks" ? "Tow / Big-Wave Gun" : "Big-Wave Gun",
    length: "8'6\" – 10'0\"+",
    volume: "45L+",
    fins: "Thruster / Tow setup",
    why: "XXL territory — paddle gun or tow board only. Experts only.",
  };
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------
export function degToCompass(d: number) {
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(d / 22.5) % 16];
}

export function weatherLabel(code: number) {
  if (code === 0) return "Clear sky";
  if ([1, 2].includes(code)) return "Mostly sunny";
  if (code === 3) return "Overcast";
  if ([45, 48].includes(code)) return "Foggy";
  if ([51, 53, 55, 56, 57].includes(code)) return "Drizzle";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return "Rain";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "Snow";
  if ([95, 96, 99].includes(code)) return "Thunderstorm";
  return "Mixed";
}

export function conditionRating(c: Conditions) {
  const score = Math.min(10, Math.max(1, Math.round((c.waveHeight * 2) + (c.swellPeriod / 3) - (c.windSpeed / 15))));
  if (score >= 8) return { label: "Firing", color: "sunset" };
  if (score >= 6) return { label: "Fun", color: "primary" };
  if (score >= 4) return { label: "Surfable", color: "secondary" };
  return { label: "Flat / Junky", color: "muted" };
}

export function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// ---------------------------------------------------------------------------
// Skill ratings
// ---------------------------------------------------------------------------
export type SkillLevel = "Beginner" | "Intermediate" | "Advanced";
export type SkillRating = "Great" | "Good" | "OK" | "Risky" | "No-go";

const RATING_COLORS: Record<SkillRating, string> = {
  Great:   "bg-emerald-500/20 text-emerald-700 border-emerald-500/40",
  Good:    "bg-primary/20 text-primary border-primary/40",
  OK:      "bg-amber-500/20 text-amber-700 border-amber-500/40",
  Risky:   "bg-orange-500/20 text-orange-700 border-orange-500/40",
  "No-go": "bg-destructive/20 text-destructive border-destructive/40",
};

export function ratingColor(r: SkillRating) {
  return RATING_COLORS[r];
}

const SKILL_CEILING: Record<SkillLevel, number> = {
  Beginner:     1.0,
  Intermediate: 2.0,
  Advanced:     6.0,
};
const SKILL_FLOOR: Record<SkillLevel, number> = {
  Beginner:     0.3,
  Intermediate: 0.5,
  Advanced:     0.8,
};

const SPOT_DIFFICULTY_MIN: Record<Surfbreak["difficulty"], SkillLevel | "Pro"> = {
  Beginner:     "Beginner",
  Intermediate: "Intermediate",
  Advanced:     "Advanced",
  Pro:          "Pro",
};

export function skillRating(c: Conditions, b: Surfbreak, level: SkillLevel): SkillRating {
  const h    = c.waveHeight;
  const wind = c.windSpeed;
  const ceiling = SKILL_CEILING[level];
  const floor   = SKILL_FLOOR[level];
  const spotMin = SPOT_DIFFICULTY_MIN[b.difficulty];

  if (spotMin === "Pro" && level !== "Advanced") return "No-go";
  if (spotMin === "Advanced" && level === "Beginner") return "No-go";
  if (spotMin === "Intermediate" && level === "Beginner" && h > 0.8) return "Risky";

  if (h < floor) return "OK";
  if (h > ceiling * 1.4) return "No-go";
  if (h > ceiling) return "Risky";

  let score = 3;
  if (h >= floor && h <= ceiling * 0.85) score = 4;
  if (wind > 30) score -= 2;
  else if (wind > 20) score -= 1;

  if (score >= 4) return "Great";
  if (score === 3) return "Good";
  if (score === 2) return "OK";
  if (score === 1) return "Risky";
  return "No-go";
}

export function allSkillRatings(c: Conditions, b: Surfbreak) {
  return {
    Beginner:     skillRating(c, b, "Beginner"),
    Intermediate: skillRating(c, b, "Intermediate"),
    Advanced:     skillRating(c, b, "Advanced"),
  } as Record<SkillLevel, SkillRating>;
}

// ---------------------------------------------------------------------------
// Personalised board recommendation
// ---------------------------------------------------------------------------
export type RiderStyle = "Cruise" | "All-round" | "Performance";

export type BoardCriteria = {
  level: SkillLevel;
  weightKg: number;
  style: RiderStyle;
};

const VOL_PER_KG: Record<SkillLevel, number> = {
  Beginner:     0.55,
  Intermediate: 0.42,
  Advanced:     0.34,
};

export function recommendBoardForUser(
  c: Conditions,
  b: Surfbreak,
  crit: BoardCriteria,
): BoardRec & { volumeLiters: number; suitability: SkillRating } {
  const base = recommendBoard(c, b);
  const h = c.waveHeight;

  const styleBias = crit.style === "Cruise" ? 1.12 : crit.style === "Performance" ? 0.92 : 1.0;
  const wavesBias = h < 1 ? 1.08 : h > 2.5 ? 0.95 : 1.0;

  const liters = Math.round(crit.weightKg * VOL_PER_KG[crit.level] * styleBias * wavesBias);

  let type   = base.type;
  let length = base.length;
  let fins   = base.fins;
  let emoji  = base.emoji;
  let why    = base.why;

  if (crit.level === "Beginner") {
    if (h < 1.2) {
      type   = "Soft-top Longboard";
      length = "8'0\" – 9'0\"";
      fins   = "2+1 soft fins";
      emoji  = "🏖️";
      why    = "Foamy, forgiving and floaty — catch tons of waves and learn to pop up safely.";
    } else {
      type   = "Funboard / Mini-mal";
      length = "7'2\" – 7'8\"";
      fins   = "2+1";
      emoji  = "🐢";
      why    = "Punchy waves on a stable platform — paddle power without an unwieldy log.";
    }
  } else if (crit.style === "Cruise") {
    if (h < 1.5) {
      type   = "Mid-length / Egg";
      length = "7'0\" – 7'6\"";
      fins   = "2+1 or Twin";
      emoji  = "🥚";
      why    = "Glide-focused build — flowy lines, easy paddle, fun in everything chest-high or under.";
    }
  } else if (crit.style === "Performance" && crit.level === "Advanced" && h >= 1.2) {
    type   = "High-Performance Shortboard";
    length = h > 2.2 ? "6'0\" – 6'4\"" : "5'10\" – 6'2\"";
    fins   = "Thruster";
    emoji  = "⚡";
    why    = "Tight rocker, thin rails — full send on clean faces.";
  }

  const suitability = skillRating(c, b, crit.level);
  return {
    type,
    length,
    fins,
    emoji,
    why,
    volume: `${liters}L (sized for ${crit.weightKg}kg)`,
    volumeLiters: liters,
    suitability,
  };
}
